import React, { type FC } from 'react'

import { useIntl } from 'react-intl'

import {
  Flex,
  FlexItem,
  LINK_VARIANT_EXTRALIGHT,
  Link,
  Panel,
  SKELETON_LOADER_TYPE_CIRCLE,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
  TEXT_VARIANT_ITALIC_LIGHT,
  TITLE_VARIANT_L,
  Text,
  Title,
} from 'mailup-web-components'

import RenderWhen from 'Components/RenderWhen'

import Chart from 'Pages/RecipientsOverview/components/Chart'
import NoChartData from 'Pages/RecipientsOverview/components/NoChartData'
import type { ChartItemData } from 'Pages/RecipientsOverview/types'

const MemoChart = React.memo(Chart) //  prevent chart from not redrawing

interface ChartWidgetProps {
  chartId: string
  title: string
  isEmptyState: boolean
  data: ChartItemData[]
  status: 'initial' | 'error' | 'ready'
  loading: boolean
  onReload: () => void | Promise<void>
}

const ChartWidget: FC<ChartWidgetProps> = ({
  chartId,
  title,
  isEmptyState,
  data,
  status,
  loading,
  onReload,
}) => {
  const intl = useIntl()

  return (
    <Flex flexDirection="column" gap="16px">
      <Title variant={TITLE_VARIANT_L}>{title}</Title>

      <Panel margin="0">
        <RenderWhen condition={loading}>
          <Flex gap="64px" justifyContent="center">
            <FlexItem>
              <SkeletonLoader
                type={SKELETON_LOADER_TYPE_CIRCLE}
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="270px"
                height="270px"
                margin="0px"
              />
            </FlexItem>
            <Flex
              flexDirection="column"
              alignItems="flex-start"
              justifyContent="center"
              height="270px"
              width="320px"
            >
              <SkeletonLoader
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="100px"
                height="20px"
                margin="0 0 8px 0"
              />
              <SkeletonLoader
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="100px"
                height="20px"
                margin="0 0 8px 0"
              />
              <SkeletonLoader
                variant={SKELETON_LOADER_VARIANT_ONWHITE}
                width="100px"
                height="20px"
                margin="0 0 8px 0"
              />
            </Flex>
          </Flex>
        </RenderWhen>

        <RenderWhen condition={status === 'error'}>
          <Flex alignItems="center" justifyContent="center">
            <Text variant={TEXT_VARIANT_ITALIC_LIGHT}>
              {intl.formatMessage({ id: 'Load.Common.LoadDataError' })}
            </Text>
            <Link variant={LINK_VARIANT_EXTRALIGHT} onClick={onReload}>
              {intl.formatMessage({ id: 'Load.Common.RealoadPage' })}
            </Link>
          </Flex>
        </RenderWhen>

        <RenderWhen condition={status === 'ready'}>
          <RenderWhen condition={!isEmptyState}>
            <MemoChart id={chartId} data={data} title="" />
          </RenderWhen>
          <RenderWhen condition={isEmptyState}>
            <NoChartData />
          </RenderWhen>
        </RenderWhen>
      </Panel>
    </Flex>
  )
}
export default ChartWidget
